import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson, getToken, formatApiError } from '../api/client.js';
import { Modal } from './Modal.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { formatDateTime } from '../utils/formatDate.js';
import { runPdfDocumentImport } from '../utils/runPdfDocumentImport.js';

/**
 * Compact PDF documents panel for project/task pages.
 * @param {{ projectId?: number|string, taskId?: number|string, clientId?: number|string }} props
 */
export function PdfDocumentsPanel({ projectId, taskId, clientId }) {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisUrl, setAnalysisUrl] = useState('');

  const query = () => {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', String(projectId));
    if (taskId) params.set('task_id', String(taskId));
    return params.toString() ? `?${params}` : '';
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await apiJson(`/api/admin/pdf-documents${query()}`));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [projectId, taskId]);

  const attachMeta = (target) => {
    if (clientId) target.client_id = Number(clientId);
    if (projectId) target.project_id = Number(projectId);
    if (taskId) target.task_id = Number(taskId);
    return target;
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    setUploadStatus('Uploading PDF…');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', file.name.replace(/\.pdf$/i, ''));
      if (clientId) form.append('client_id', String(clientId));
      if (projectId) form.append('project_id', String(projectId));
      if (taskId) form.append('task_id', String(taskId));
      const created = await apiJson('/api/admin/pdf-documents', { method: 'POST', body: form });
      if (!created?.id) {
        throw new Error('Upload succeeded but the document id was missing. Refresh and open it from the list.');
      }
      await runPdfDocumentImport(created.id, {
        title: created.title || file.name,
        onStatus: setUploadStatus,
      });
      window.location.href = `/admin/pdf-documents/${created.id}`;
    } catch (err) {
      setError(err.message);
      setUploadStatus('');
      setBusy(false);
    }
  };

  const createBlank = async () => {
    setBusy(true);
    setError('');
    try {
      const created = await apiJson('/api/admin/pdf-documents/blank', {
        method: 'POST',
        body: attachMeta({ title: 'Untitled document', pages: 1 }),
      });
      if (!created?.id) {
        throw new Error('Document was created but the id was missing. Refresh and open it from the list.');
      }
      window.location.href = `/admin/pdf-documents/${created.id}`;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const createAnalysis = async (e) => {
    e.preventDefault();
    if (!analysisUrl.trim()) {
      setError('Enter a website URL.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const created = await apiJson('/api/admin/pdf-documents/site-analysis', {
        method: 'POST',
        body: attachMeta({ url: analysisUrl.trim() }),
      });
      if (!created?.id) {
        throw new Error('Analysis finished but the document id was missing. Refresh and open it from the list.');
      }
      window.location.href = `/admin/pdf-documents/${created.id}`;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-header-row" style={{ marginBottom: '0.75rem' }}>
        <div>
          <strong>PDF documents</strong>
          <p className="muted small" style={{ marginBottom: 0 }}>
            Blank PDFs, website analysis reports, or uploads. Share from the editor.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => void createBlank()}>
            Blank
          </button>
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => setShowAnalysis(true)}>
            Analysis
          </button>
          <label className="btn btn-sm btn-primary" style={{ marginBottom: 0 }}>
            {busy ? (uploadStatus || 'Working…') : 'Upload'}
            <input type="file" accept="application/pdf,.pdf" hidden disabled={busy} onChange={onUpload} />
          </label>
          <Link
            className="btn btn-sm btn-outline-secondary"
            to={`/admin/pdf-documents${projectId ? `?project_id=${projectId}` : ''}`}
          >
            View all
          </Link>
        </div>
      </div>
      {error ? <p className="err">{error}</p> : null}
      {busy && uploadStatus ? <p className="muted small">{uploadStatus}</p> : null}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No PDFs attached yet.</p>
      ) : (
        <ul className="list-unstyled mb-0">
          {items.map((doc) => (
            <li key={doc.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
              <Link to={`/admin/pdf-documents/${doc.id}`}>{doc.title}</Link>
              <div className="muted small">
                {doc.visibility}
                {doc.updated_at ? ` · ${formatDateTime(doc.updated_at)}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={showAnalysis} title="Create website analysis" onClose={() => !busy && setShowAnalysis(false)}>
        <form className="stack" onSubmit={createAnalysis}>
          <p className="muted small" style={{ marginTop: 0 }}>
            Crawl the site and generate an OpenAI improvement report PDF.
          </p>
          {!caps.openai ? <p className="err">OpenAI integration is required.</p> : null}
          <label className="field">
            <span>Website URL</span>
            <input value={analysisUrl} onChange={(e) => setAnalysisUrl(e.target.value)} placeholder="https://example.com" required />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setShowAnalysis(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || !caps.openai}>
              {busy ? 'Analyzing…' : 'Create analysis'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/**
 * @param {number} id
 * @param {'admin'|'my'} scope
 * @param {{ source?: boolean }} [options]
 */
export async function downloadPdfDocument(id, scope = 'admin', options = {}) {
  const preferSource = Boolean(options.source) && scope === 'admin';
  const path = scope === 'my'
    ? `/api/my/pdf-documents/${id}/download`
    : preferSource
      ? `/api/admin/pdf-documents/${id}/download-source`
      : `/api/admin/pdf-documents/${id}/download`;
  const headers = { Accept: '*/*' };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;
  const res = await fetch(path, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatApiError(data, res.status));
  }
  return res.blob();
}
